import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import {
  CreateInvitationPayload,
  Invitation,
  InvitationDTO,
  InvitationId,
  PublicInvitation,
  PublicInvitationDTO,
  createInvitationPayloadSchema,
  invitationSchema,
  publicInvitationSchema,
} from '@optical/types';

@Injectable({ providedIn: 'root' })
export class InvitationService {
  private readonly http = inject(HttpClient);

  async createInvitation(payload: CreateInvitationPayload): Promise<Invitation> {
    const dto = createInvitationPayloadSchema.encode(payload);
    const response = await firstValueFrom(this.http.post<InvitationDTO>('/invitations', dto));

    return invitationSchema.decode(response);
  }

  async deleteInvitation(invitationId: InvitationId): Promise<void> {
    await firstValueFrom(this.http.delete<InvitationDTO>(`/invitations/${invitationId}`));
  }

  async getInvitations(): Promise<Invitation[]> {
    const response = await firstValueFrom(this.http.get<InvitationDTO[]>('/invitations'));

    return response.map((dto) => invitationSchema.decode(dto));
  }

  /**
   * Resolves an invitation from a signup link.
   *
   * Public: callable before sign-in, and so answers with the redacted {@link PublicInvitation}
   * rather than the full entity.
   */
  async getInvitation(token: string): Promise<PublicInvitation | null> {
    try {
      const response = await firstValueFrom(
        this.http.get<PublicInvitationDTO>(`/invitations/${token}`),
      );

      return publicInvitationSchema.decode(response);
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 404) {
        return null;
      }

      throw error;
    }
  }
}
